// React
import { useContext } from "react"

// Router
import { Routes } from "@/constants/routes"

// UI
import { cn } from "@/lib/utils"
import { Menu, MenuButton, MenuItems, MenuItem, Transition } from "@/lib/ui"
import { Link } from "@/components/Anchor"

// Icons
import { LogoutIcon, FiUser, FiChevronDown, FiChevronUp } from "@/lib/icons"

// Animations
import { getTransitionPopoverProps } from "@/lib/animations"

import { UserRoles } from "@/constants/constants"

// Translations
import { useTrans } from "@/i18n"

// Hooks
import { useDeviceCssType } from "@/lib/device"
import { useAuth } from "@/context/useAuth"
import { useToasts } from "@/context/useToasts"

// Analytics
import { sendEvent } from "@/lib/analytics"

// Assets
import BrandLogo from "@/assets/icons/brand/zonhub_logo.svg?react"

// Context
import { useCurrentUserInfo, useCurrentUserRoles } from "@/context/user"
import { RedirectContext } from "@/context/redirectContext"

// Env variables
import { API_URL, EXTERNAL_PLATFORM_ADMIN_PANEL } from "@/lib/env"

/**
 * UserMenu
 * @returns
 */
export function UserMenu() {
	// Translations
	const t = useTrans()

	// Hooks
	const auth = useAuth()
	const toasts = useToasts()
	const { hasOneOfRoles } = useCurrentUserRoles()
	const { first_name, last_name } = useCurrentUserInfo()
	const deviceTypeCss = useDeviceCssType()

	// Context
	const { redirect } = useContext(RedirectContext)

	return (
		<Menu as="div" className="relative order-1 md:order-2">
			{({ open }) => (
				<>
					<MenuButton
						className={cn(
							deviceTypeCss === "desktop"
								? "cursor-default"
								: "cursor-pointer",
							"flex w-full px-2",
						)}
						onClick={() => {
							sendEvent("layout", "usermenu_icon_onclick")
						}}
					>
						<div
							className={cn(
								"relative block overflow-hidden rounded-[50%] border-2 border-gray-400 bg-white",
								"mr-3 h-6 w-6", // Sizes responsive
							)}
						>
							<span className="sr-only">
								{t("common:layout.open_user_menu")}
							</span>
							<UserIcon className="absolute -bottom-1 left-1/2 size-[95%] -translate-x-1/2 fill-gray-400 text-gray-400" />
						</div>

						<p className="text-md block truncate text-left text-gray-400">
							{first_name} {last_name}
						</p>

						{/** Show arrow icon on mobile */}
						{deviceTypeCss === "mobile" && (
							<span className="ml-auto flex items-center">
								{open === true ? (
									<FiChevronDown className="size-4 text-gray-400" />
								) : (
									<FiChevronUp className="size-4 text-gray-400" />
								)}
							</span>
						)}
					</MenuButton>

					<Transition
						{...getTransitionPopoverProps()}
						show={open || deviceTypeCss === "desktop"} // Do not transition on desktop
					>
						<MenuItems
							static={deviceTypeCss === "desktop"} // Always show on desktop
							className={cn(
								"right-0 z-50 mt-2 w-full origin-top-right rounded-md pt-1 pb-1 focus:outline-hidden",
							)}
						>
							{/* Menu items */}
							<>
								{/** Admin items */}
								{hasOneOfRoles(UserRoles.admin) && (
									<>
										{/** App information */}
										<MenuItem>
											<Link
												href={Routes.Info}
												className={cn(
													"hover:bg-gray-100",
													"block px-4 py-2 text-sm text-gray-700",
												)}
												onClick={() =>
													sendEvent(
														"layout",
														"usermenu_item_onclick",
														{
															label: "App information",
														},
													)
												}
											>
												App information
											</Link>
										</MenuItem>

										<MenuItem>
											<button
												onClick={() => {
													sendEvent(
														"layout",
														"usermenu_item_onclick",
														{
															label: "Admin panel",
														},
													)
													// Redirect to admin panel
													redirect(
														EXTERNAL_PLATFORM_ADMIN_PANEL,
													)
												}}
												className={cn(
													"cursor-pointer hover:bg-gray-100",
													"block w-full px-4 py-2 text-left text-sm text-gray-700",
												)}
											>
												Admin panel
											</button>
										</MenuItem>
									</>
								)}

								{/** Logout */}
								<MenuItem>
									<button
										onClick={() => {
											sendEvent(
												"layout",
												"usermenu_item_onclick",
												{
													label: t(
														"common:layout.quick_menu.sign_out",
													),
												},
											)
											// Try logout
											try {
												void auth.logOut()
											} catch (e) {
												console.error(
													"Could not log out",
													e,
												)
												toasts.addToast({
													id: "common:layout.quick_menu.sign_out",
													text: "Failed to log out, try again later.",
												})
											}
										}}
										className={cn(
											"cursor-pointer hover:bg-gray-100",
											"flex w-full items-center px-4 py-2 text-sm text-gray-700",
										)}
									>
										<LogoutIcon
											className="mr-3 size-5 text-gray-400"
											aria-hidden="true"
										/>
										{t("common:layout.quick_menu.sign_out")}
									</button>
								</MenuItem>
							</>
						</MenuItems>
					</Transition>
				</>
			)}
		</Menu>
	)
}

export function UserIcon({ className = "" }: { className?: string }) {
	return (
		<>
			<FiUser
				className={cn(
					className,
					"h-[95%] w-[95%] fill-gray-400 text-gray-400",
				)}
			/>
		</>
	)
}

/**
 * Logo
 * @returns
 */
export function Logo() {
	const t = useTrans()
	return (
		<a
			href={API_URL}
			className="block h-full py-3"
			aria-label={t("common:web.homepage")}
		>
			<BrandLogo className="h-full brightness-0 grayscale" />
		</a>
	)
}
